import { type LoadableResponse, type Restaurant } from "api-types";
import { User } from "firebase/auth";
import { useDeviceDimensions } from "igfd-ui";

import React, { PropsWithChildren } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { Box, Drawer } from "@mui/material";

import { useDisclosure } from "@dashboard/hooks/useDisclosure";
import { useIgdfMenuUrl } from "@dashboard/queries/igfdMenuUrls";
import { type ReduxState } from "@dashboard/types";

import { HeaderComponent } from "../header/HeaderComponent";
import { HeaderSidebarComponent } from "../headerSidebar/HeaderSidebarComponent";

export type AppScaffoldProps = PropsWithChildren<{
  pathname: string;
  navigate: (to: string) => void;
  logout: () => void;
  managerDisplayName?: string;
  managerPhotoUrl?: string;
  selectedRestaurant: Restaurant | null;
  menuUrl: LoadableResponse<string>;
}>;

/**
 * Renders the authenticated app, with Headers, Sidebar, and the main content.
 */
export const AppScaffoldComponent = ({
  children,
  pathname,
  navigate,
  managerDisplayName,
  managerPhotoUrl,
  selectedRestaurant,
  menuUrl,
  logout,
}: AppScaffoldProps) => {
  const { isLargeScreen } = useDeviceDimensions();

  const { isOpen: isLeftDrawerOpen, toggle: toggleLeftDrawer } = useDisclosure({
    isDefaultOpen: false,
  });

  return (
    <Box sx={{ height: "100vh", width: "100vw" }}>
      <HeaderComponent
        isLargeScreen={isLargeScreen}
        isLeftDrawerOpen={isLeftDrawerOpen}
        toggleLeftDrawer={toggleLeftDrawer}
        managerDisplayName={managerDisplayName}
        managerPhotoUrl={managerPhotoUrl}
        selectedRestaurant={selectedRestaurant}
        menuUrl={menuUrl}
        logout={logout}
        navigate={navigate}
      />
      {selectedRestaurant && (
        <nav>
          <Drawer
            anchor={"left"}
            variant={isLargeScreen ? "permanent" : "temporary"}
            open={isLargeScreen || isLeftDrawerOpen}
            onClose={toggleLeftDrawer}
            PaperProps={{
              sx: ({ spacing, palette }) =>
                isLargeScreen
                  ? {
                      // set the height of the drawer to the height
                      // of the viewport minus the height of the header
                      // in large screens
                      height: `calc(100% - ${spacing(8)} + 1px)`,
                      top: `calc(${spacing(8)} + 1px)`,
                      borderRight: `1px solid ${palette.grey[400]}`,
                    }
                  : {},
            }}
          >
            <HeaderSidebarComponent
              isLargeScreen={isLargeScreen}
              isLeftDrawerOpen={isLeftDrawerOpen}
              toggleLeftDrawer={toggleLeftDrawer}
              pathname={pathname}
              navigate={navigate}
              selectedRestaurant={selectedRestaurant}
            />
          </Drawer>
        </nav>
      )}
      <Box
        component="main"
        sx={({ spacing }) => ({
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          paddingTop: spacing(8),
          paddingLeft: isLargeScreen ? spacing(11) : 0, // the sidebar is hidden on small screens
        })}
      >
        {children}
      </Box>
    </Box>
  );
};

export const AppScaffold = ({
  children,
  user,
  logout,
}: PropsWithChildren<{
  logout: () => void;
  user: User;
}>) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const selectedRestaurant = useSelector(
    (state: ReduxState) => state.restaurant.selectedRestaurant
  );
  const menuUrl = useIgdfMenuUrl(
    selectedRestaurant ? selectedRestaurant.uuid : undefined
  );

  return (
    <AppScaffoldComponent
      pathname={pathname}
      navigate={navigate}
      managerDisplayName={user.displayName || undefined}
      managerPhotoUrl={user.photoURL || undefined}
      selectedRestaurant={selectedRestaurant}
      menuUrl={menuUrl}
      logout={logout}
    >
      {children}
    </AppScaffoldComponent>
  );
};
